import Spinner from '@components/Spinner'
import TransportModeIcon from '@components/TransportModeIcon'
import { GetTravelSegmentResponse } from '@orbit/api-types'
import { white } from '@/theme/colors'
import { iconSizes } from '@/theme/tokens'
import React from 'react'
import { GapState } from '../core/timeline'
import { directionsUrl, formatSegmentDistance, formatSegmentDuration, pickSegmentRoute } from '../segmentDisplay'
import { DirectionsLink, GapRowRoot, RailColumn, TravelDot, TravelNode, TravelText } from './DayPane.styles'

export interface TravelRowProps {
	state: GapState
	segment: GetTravelSegmentResponse | null
}

/**
 * The gap between two consecutive events: the route's time and distance with a
 * Directions link, a spinner while the server works the route out, or just
 * the rail line when the two events share a place.
 */
const TravelRow: React.FC<TravelRowProps> = ({ state, segment }) => {
	if (state === 'hidden') {
		return (
			<GapRowRoot $state="hidden" aria-hidden="true">
				<RailColumn />
				<span />
			</GapRowRoot>
		)
	}

	if (state === 'calculating') {
		return (
			<GapRowRoot $state="calculating">
				<RailColumn>
					<TravelNode>
						<Spinner size={iconSizes.xs} color={white} />
					</TravelNode>
				</RailColumn>
				<TravelText>Calculating route…</TravelText>
			</GapRowRoot>
		)
	}

	// A segment the router had nothing for still reads as a gap: "ETA N/A", with no link to follow.
	const route = segment ? pickSegmentRoute(segment) : null
	const hasData = route !== null && (route.durationSeconds !== null || route.distanceMeters !== null)

	return (
		<GapRowRoot $state="route">
			<RailColumn>
				<TravelNode aria-hidden="true">
					<TransportModeIcon mode={route?.transportMode ?? segment?.selectedMode ?? ''} size={iconSizes.xs} />
				</TravelNode>
			</RailColumn>
			<TravelText>
				{route && hasData ? (
					<>
						<span>{formatSegmentDuration(route.durationSeconds)}</span>
						<TravelDot aria-hidden="true" />
						<span>{formatSegmentDistance(route.distanceMeters)}</span>
					</>
				) : (
					<span>ETA N/A</span>
				)}
				{route && (
					<DirectionsLink
						type="button"
						onClick={() => window.open(directionsUrl(route), '_blank', 'noopener,noreferrer')}
					>
						Directions →
					</DirectionsLink>
				)}
			</TravelText>
		</GapRowRoot>
	)
}

export default TravelRow
